import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useGeocoding, useMapStyle } from '../discover/map/hooks';
import { getTileLayerConfig } from '../discover/map/leafletTiles';
import { mapContainerStyle } from '../discover/map/constants';
import { safeInvalidateMapSize } from '../discover/map/leafletMapUtils';
import { buildEventCategoryMarkerIcon } from '../discover/map/eventCategoryMarkerIcon';
import '../discover/Map.css';
import './EventMap.css';

function EventMapNavigateIcon() {
  return (
    <svg className="event-map-navigate-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M12 3.8a8.2 8.2 0 1 0 0 16.4 8.2 8.2 0 0 0 0-16.4Z" />
      <path d="m15.7 8.3-2.15 5.25L8.3 15.7l2.15-5.25L15.7 8.3Z" />
      <path d="M12 2.2v1.6M12 20.2v1.6M21.8 12h-1.6M3.8 12H2.2" />
    </svg>
  );
}

function EventMap({ event, location, coordinates: initialCoordinates }) {
  const { t } = useTranslation(['events']);
  const [coordinates, setCoordinates] = useState(initialCoordinates || null);
  const [loading, setLoading] = useState(!initialCoordinates);
  const mapStyle = useMapStyle();
  const paletteClass = `map-palette map-palette--${mapStyle}`;
  const mapRef = useRef(null);
  const paneRef = useRef(null);
  const tileRef = useRef(null);
  const markerRef = useRef(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const { geocodeSingleEvent } = useGeocoding();

  useEffect(() => {
    const geocodeLocation = async () => {
      if (initialCoordinates) {
        setCoordinates(initialCoordinates);
        setLoading(false);
        return;
      }

      if (!location || !event) {
        setLoading(false);
        return;
      }

      try {
        const eventWithLocation = { ...event, location };
        const geocodedEvent = await geocodeSingleEvent(eventWithLocation);

        if (geocodedEvent && geocodedEvent.coordinates) {
          setCoordinates(geocodedEvent.coordinates);
        }
      } catch (err) {
        console.warn('Error geocoding location:', err);
      } finally {
        setLoading(false);
      }
    };

    geocodeLocation();
  }, [location, initialCoordinates, event, geocodeSingleEvent]);

  const createMarkerIcon = useCallback(() => {
    const pulseSeed = event?.id ?? `event-map-${event?.event_type || 'other'}`;
    return buildEventCategoryMarkerIcon(
      L,
      event?.event_type || 'other',
      event?.is_free || false,
      false,
      'hb-event-map-marker',
      pulseSeed
    );
  }, [event?.event_type, event?.is_free, event?.id]);

  const updateInfoWindowPosition = useCallback(() => {
    const map = mapRef.current;
    if (!map || !coordinates) return;

    const point = map.latLngToContainerPoint([coordinates.lat, coordinates.lng]);
    setInfoWindowPosition({ x: point.x, y: point.y });
  }, [coordinates]);

  useEffect(() => {
    const el = paneRef.current;
    if (!el || !coordinates || mapRef.current) {
      return undefined;
    }

    const map = L.map(el, {
      center: [coordinates.lat, coordinates.lng],
      zoom: 15,
      minZoom: 10,
      maxZoom: 18,
      scrollWheelZoom: true,
      zoomControl: true,
    });

    const tiles = getTileLayerConfig(mapStyle);
    tileRef.current = L.tileLayer(tiles.url, {
      attribution: tiles.attribution,
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([coordinates.lat, coordinates.lng], {
      icon: createMarkerIcon(),
      title: event?.title || location,
    }).addTo(map);

    marker.on('click', () => {
      const point = map.latLngToContainerPoint([coordinates.lat, coordinates.lng]);
      setInfoWindowPosition({ x: point.x, y: point.y });
      setShowInfoWindow(true);
    });
    markerRef.current = marker;
    mapRef.current = map;

    requestAnimationFrame(() => {
      safeInvalidateMapSize(map, { animate: false, pan: false });
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      tileRef.current = null;
    };
    // mapStyle - osobny useEffect aktualizuje kafelki (bez przebudowy całej mapy)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, createMarkerIcon, event?.title, location]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !showInfoWindow) return undefined;

    updateInfoWindowPosition();
    map.on('move zoom resize', updateInfoWindowPosition);

    return () => {
      map.off('move zoom resize', updateInfoWindowPosition);
    };
  }, [showInfoWindow, updateInfoWindowPosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !tileRef.current) return;
    const tiles = getTileLayerConfig(mapStyle);
    tileRef.current.setUrl(tiles.url);
    tileRef.current.options.attribution = tiles.attribution;
  }, [mapStyle]);

  const handleNavigate = () => {
    if (!coordinates) return;
    const { lat, lng } = coordinates;
    const q = encodeURIComponent(location || `${lat},${lng}`);
    window.open(`https://www.openstreetmap.org/directions?from=&to=${q}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="event-map-container">
        <div className="event-map-loading">
          <p>{t('events.info.mapLoading')}</p>
        </div>
      </div>
    );
  }

  if (!coordinates) {
    return (
      <div className="event-map-container">
        <div className="event-map-error">
          <p>{t('events.info.mapError')}</p>
        </div>
      </div>
    );
  }

  const getLocationLabel = () => {
    if (!event) {
      return t('events.info.location', 'Miejsce wydarzenia');
    }
    const eventType = event.event_type;
    switch (eventType) {
      case 'sport':
        return t('events.info.location.sport', 'Miejsce treningu');
      case 'party':
        return t('events.info.location.party', 'Miejsce imprezy');
      case 'hangout':
        return t('events.info.location.hangout', 'Miejsce spotkania');
      case 'trip':
        return t('events.info.location.trip', 'Miejsce zbiórki');
      case 'gaming':
        return t('events.info.location.gaming', 'Miejsce wydarzenia');
      default:
        return t('events.info.location', 'Miejsce wydarzenia');
    }
  };

  return (
    <div className={`event-map-container ${paletteClass}`} style={{ position: 'relative' }}>
      <div ref={paneRef} className="event-map-leaflet-pane" style={mapContainerStyle} />
      {showInfoWindow && infoWindowPosition && (
        <div
          className="event-map-info-overlay"
          style={{
            left: `${infoWindowPosition.x}px`,
            top: `${infoWindowPosition.y}px`,
          }}
        >
          <button type="button" className="event-map-info-close" onClick={() => setShowInfoWindow(false)} aria-label="Zamknij">
            ×
          </button>
          <div className="event-map-info-window">
            <div className="event-map-info-label">{getLocationLabel()}</div>
            <div className="event-map-info-address">{location}</div>
          </div>
        </div>
      )}
      <button className="event-map-navigate-button" onClick={handleNavigate} type="button" title={t('events.info.navigate')}>
        <span className="event-map-navigate-icon" aria-hidden="true">
          <EventMapNavigateIcon />
        </span>
        <span className="event-map-navigate-text">{t('events.info.navigate')}</span>
      </button>
    </div>
  );
}

export default EventMap;
